import React, { useState, useEffect } from 'react';
import { Button } from '@vaiu/ui/button';
import { Input } from '@vaiu/ui/input';
import { Label } from '@vaiu/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@vaiu/ui/input-otp';
import { ArrowLeft, Smartphone, Check, Loader2, User, Mail } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { i18n } from '@/utils/i18n';
import { platformApi } from '@/services/platformApi';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getDefaultAuthenticatedDestination } from '@/utils/domainDetection';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@vaiu/ui/select";

interface PhoneSignupProps {
  onSuccess?: (token: string) => void;
  onBack?: () => void;
  onSwitchToEmail?: () => void;
}

// Country codes with flags
const countryCodes = [
  { code: '+1', country: 'US', flag: '🇺🇸', name: 'United States' },
  { code: '+1', country: 'CA', flag: '🇨🇦', name: 'Canada' },
  { code: '+44', country: 'GB', flag: '🇬🇧', name: 'United Kingdom' },
  { code: '+33', country: 'FR', flag: '🇫🇷', name: 'France' },
  { code: '+49', country: 'DE', flag: '🇩🇪', name: 'Germany' },
  { code: '+34', country: 'ES', flag: '🇪🇸', name: 'Spain' },
  { code: '+39', country: 'IT', flag: '🇮🇹', name: 'Italy' },
  { code: '+81', country: 'JP', flag: '🇯🇵', name: 'Japan' },
  { code: '+86', country: 'CN', flag: '🇨🇳', name: 'China' },
  { code: '+91', country: 'IN', flag: '🇮🇳', name: 'India' },
  { code: '+61', country: 'AU', flag: '🇦🇺', name: 'Australia' },
  { code: '+55', country: 'BR', flag: '🇧🇷', name: 'Brazil' },
  { code: '+52', country: 'MX', flag: '🇲🇽', name: 'Mexico' },
];

const PhoneSignup: React.FC<PhoneSignupProps> = ({ onSuccess, onBack, onSwitchToEmail }) => {
  const [step, setStep] = useState<'phone' | 'verify' | 'profile'>('phone');
  const [countryCode, setCountryCode] = useState('+1');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState(0);
  const [canResend, setCanResend] = useState(false);
  
  // Profile completion fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [skipProfile, setSkipProfile] = useState(false);
  
  const { toast } = useToast();
  const { login } = useAuth();
  const navigate = useNavigate();

  // Countdown timer for resend
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && step === 'verify') {
      setCanResend(true);
    }
  }, [timeLeft, step]);

  const formatPhoneNumber = (value: string) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // Format based on US phone number for now
    if (countryCode === '+1') {
      if (digits.length <= 3) return digits;
      if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
    
    return digits;
  };

  const validatePhoneNumber = () => {
    const digits = phoneNumber.replace(/\D/g, '');
    
    // Basic validation - at least 10 digits for most countries
    if (digits.length < 10) {
      setError('Please enter a valid phone number');
      return false;
    }
    
    return true;
  };

  const checkPhoneAvailability = async () => {
    const fullNumber = countryCode + phoneNumber.replace(/\D/g, '');
    
    try {
      const response = await platformApi.post('/auth/phone/check', {
        phoneNumber: fullNumber
      });
      
      if (!response.available) {
        setError('This phone number is already registered. Please login instead.');
        return false;
      }
      
      return true;
    } catch (err) {
      // If endpoint doesn't exist yet, continue anyway
      console.warn('Phone availability check failed:', err);
      return true;
    }
  };

  const sendOTP = async () => {
    if (!validatePhoneNumber()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const fullNumber = countryCode + phoneNumber.replace(/\D/g, '');
      
      // Check if phone is available
      const isAvailable = await checkPhoneAvailability();
      if (!isAvailable) {
        setLoading(false);
        return;
      }
      
      // Send OTP for signup
      const result = await platformApi.post('/auth/signup/phone/send', {
        phoneNumber: fullNumber,
        countryCode: countryCode
      });

      if (result.success) {
        setStep('verify');
        setTimeLeft(result.resendAfter || 60);
        setCanResend(false);
        
        toast({
          title: 'Verification Code Sent',
          description: `We've sent a 6-digit code to ${countryCode} ${formatPhoneNumber(phoneNumber)}`,
        });
      }
    } catch (err: any) {
      // Handle specific error cases
      if (err.message?.includes('405') || err.message?.includes('Method Not Allowed')) {
        setError('Phone signup is not available yet. Please use email signup.');
        toast({
          title: 'Feature Coming Soon',
          description: 'Phone signup is being set up. Please use email signup for now.',
          variant: 'destructive',
        });
      } else if (err.message?.includes('already registered')) {
        setError('This phone number is already registered. Please login instead.');
      } else {
        setError(err.message || 'Failed to send verification code');
      }
    } finally {
      setLoading(false);
    }
  };

  const verifyOTPAndSignup = async () => {
    if (otp.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const fullNumber = countryCode + phoneNumber.replace(/\D/g, '');
      
      // Use the proper API method which handles validation and logging
      const result = await platformApi.verifyOtp(fullNumber, otp, 'sms');

      if (result.token) {
        // Store token and login
        await login(result.token);
        
        toast({
          title: 'Account Created!',
          description: 'Your account has been created successfully.',
        });

        // Check if profile completion is needed
        if (result.requiresProfileCompletion) {
          setStep('profile');
        } else {
          // Navigate to dashboard
          if (onSuccess) {
            onSuccess(result.token);
          } else {
            navigate(getDefaultAuthenticatedDestination());
          }
        }
      }
    } catch (err: any) {
      if (err.message?.includes('Invalid code')) {
        setError('Invalid verification code. Please try again.');
        setOtp('');
      } else if (err.message?.includes('expired')) {
        setError('Verification code has expired. Please request a new one.');
        setOtp('');
      } else {
        setError(err.message || 'Verification failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const completeProfile = async () => {
    setLoading(true);
    setError('');

    try {
      // Profile completion is optional
      if (!skipProfile && (name || email)) {
        await platformApi.put('/user/profile', {
          name: name.trim(),
          email: email.trim(),
        });
        
        toast({
          title: 'Profile Updated',
          description: 'Your profile has been updated successfully.',
        });
      }
      
      // Navigate to dashboard
      navigate(getDefaultAuthenticatedDestination());
    } catch (err: any) {
      // Profile update failure is not critical
      console.error('Profile update failed:', err);
      // Still navigate to dashboard
      navigate(getDefaultAuthenticatedDestination());
    } finally {
      setLoading(false);
    }
  };

  const resendOTP = async () => {
    if (!canResend) return;
    
    setCanResend(false);
    setTimeLeft(60);
    setError('');
    
    try {
      const fullNumber = countryCode + phoneNumber.replace(/\D/g, '');
      
      await platformApi.post('/auth/signup/phone/send', {
        phoneNumber: fullNumber,
        countryCode: countryCode
      });
      
      toast({
        title: 'Code Resent',
        description: 'A new verification code has been sent.',
      });
    } catch (err: any) {
      setError('Failed to resend code. Please try again.');
      setCanResend(true);
    }
  };

  const renderPhoneEntry = () => (
    <div className="space-y-4">
      <div className="text-center">
        <h3 className="text-lg font-semibold mb-2">Sign up with Phone Number</h3>
        <p className="text-sm text-muted-foreground">
          Create your account using your mobile number
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="phone">Phone Number</Label>
        <div className="flex space-x-2">
          <Select value={countryCode} onValueChange={setCountryCode}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {countryCodes.map((country) => (
                <SelectItem key={`${country.code}-${country.country}`} value={country.code}>
                  <span className="flex items-center space-x-2">
                    <span>{country.flag}</span>
                    <span>{country.code}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Input
            id="phone"
            type="tel"
            placeholder="(555) 123-4567"
            value={formatPhoneNumber(phoneNumber)}
            onChange={(e) => setPhoneNumber(e.target.value)}
            className={error ? 'border-red-500' : ''}
            disabled={loading}
          />
        </div>
        {error && (
          <p className="text-sm text-red-600">{error}</p>
        )}
        <p className="text-xs text-muted-foreground">
          We'll send you a verification code via SMS
        </p>
      </div>

      <div className="space-y-3">
        <Button 
          onClick={sendOTP} 
          className="w-full" 
          disabled={loading || !phoneNumber}
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Sending...
            </>
          ) : (
            <>
              Continue
              <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
            </>
          )}
        </Button>

        {onSwitchToEmail && (
          <Button 
            variant="outline" 
            onClick={onSwitchToEmail}
            className="w-full"
            disabled={loading}
          >
            <Mail className="w-4 h-4 mr-2" />
            Sign up with Email instead
          </Button>
        )}
        
        {onBack && (
          <Button 
            variant="ghost" 
            onClick={onBack}
            className="w-full"
            disabled={loading}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        )}
      </div>

      <div className="text-center text-sm text-muted-foreground">
        Already have an account?{' '}
        <Button
          variant="link"
          className="p-0 h-auto text-blue-600"
          onClick={() => navigate('/login')}
        >
          Log in
        </Button>
      </div>
    </div>
  );

  const renderVerification = () => (
    <div className="space-y-4">
      <div className="text-center">
        <h3 className="text-lg font-semibold mb-2">Verify Your Number</h3>
        <p className="text-sm text-muted-foreground">
          Enter the 6-digit code sent to
        </p>
        <p className="text-sm font-medium">
          {countryCode} {formatPhoneNumber(phoneNumber)}
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="otp" className="text-center block">Verification Code</Label>
        <InputOTP
          value={otp}
          onChange={setOtp}
          maxLength={6}
          disabled={loading}
        >
          <InputOTPGroup className="justify-center">
            <InputOTPSlot index={0} />
            <InputOTPSlot index={1} />
            <InputOTPSlot index={2} />
            <InputOTPSlot index={3} />
            <InputOTPSlot index={4} />
            <InputOTPSlot index={5} />
          </InputOTPGroup>
        </InputOTP>
        {error && (
          <Alert variant="destructive" className="mt-2">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </div>

      <div className="text-center text-sm text-muted-foreground">
        {timeLeft > 0 ? (
          <span>Resend code in {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}</span>
        ) : (
          <Button
            variant="link"
            className="p-0 h-auto"
            onClick={resendOTP}
            disabled={!canResend || loading}
          >
            Resend verification code
          </Button>
        )}
      </div>

      <div className="space-y-3">
        <Button 
          onClick={verifyOTPAndSignup} 
          className="w-full" 
          disabled={loading || otp.length !== 6}
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Verifying...
            </>
          ) : (
            <>
              <Check className="w-4 h-4 mr-2" />
              Verify & Create Account
            </>
          )}
        </Button>

        <Button 
          variant="ghost" 
          onClick={() => {
            setStep('phone');
            setOtp('');
            setError('');
          }}
          className="w-full"
          disabled={loading}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Change Phone Number
        </Button>
      </div>
    </div>
  );

  const renderProfileCompletion = () => (
    <div className="space-y-4">
      <div className="text-center">
        <h3 className="text-lg font-semibold mb-2">Complete Your Profile</h3>
        <p className="text-sm text-muted-foreground">
          Optional: Add more details to personalize your account
        </p>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="name">
            <User className="w-4 h-4 inline mr-1" />
            Name (optional)
          </Label>
          <Input
            id="name"
            type="text"
            placeholder="John Doe"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={loading}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">
            <Mail className="w-4 h-4 inline mr-1" />
            Email (optional - for account recovery)
          </Label>
          <Input
            id="email"
            type="email"
            placeholder="john@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
          />
          <p className="text-xs text-muted-foreground">
            Adding an email helps with account recovery
          </p>
        </div>
      </div>

      <div className="space-y-3">
        <Button 
          onClick={completeProfile} 
          className="w-full" 
          disabled={loading}
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            'Complete Setup'
          )}
        </Button>

        <Button 
          variant="outline" 
          onClick={() => {
            setSkipProfile(true);
            completeProfile();
          }}
          className="w-full"
          disabled={loading}
        >
          Skip for now
        </Button>
      </div>
    </div>
  );

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center justify-center space-x-2">
          <Smartphone className="w-5 h-5" />
          <span>Phone Signup</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {step === 'phone' && renderPhoneEntry()}
        {step === 'verify' && renderVerification()}
        {step === 'profile' && renderProfileCompletion()}
      </CardContent>
    </Card>
  );
};

export default PhoneSignup;
